/**
 * CJdropshipping vs 竞品专页面 — 子页面模板组件
 * 5 个竞品子页面（vs Zendrop / AutoDS / Spocket / AliExpress / Day One）共用本模板。
 *
 * 导出组件：
 *   - VsHero          — 子页 Banner（eyebrow + 标题 + proof row + 右侧图片 + 浮动卡片）
 *   - VsComparisonTable — 三列特征对比表（Feature / Competitor / CJdropshipping）
 *   - VsFaqSection    — 子页 FAQ（左侧摘要卡片 + 右侧 accordion）
 *
 * 各子页面仅需在自己的 constants.js 中填入差异文案，组件结构完全复用。
 * 样式在各子页面的 index.less 中 @import SharedComponents.less 后另行编写，
 * 或在此文件同级的 vsPageTemplate.less 中扩展。
 *
 * 注意：
 *   - VsHero.heroImage 字段由各子页面 constants.js 各自配置不同 Unsplash 图
 *   - VsFaqSection 使用 React state 控制 accordion（替代 HTML <details>，避免 SSR hydration 差异）
 *   - 默认第一条 FAQ 展开（openItems 初始值为 [0]）
 */

import React, { useContext, useState, useCallback } from 'react';
import { ConstantsContext } from './SharedComponents';

// ========== VsHero ==========

/**
 * 子页 Hero Banner
 * 从 ConstantsContext.VS_HERO 读取文案：
 * {
 *   eyebrow: string,
 *   title: string,         // 纯文字，如 "CJdropshipping vs"
 *   titleHighlight: string,// 橙色高亮部分，如 "Zendrop"
 *   subtitle: string,
 *   desc: string,
 *   proofItems: string[],  // proof row 标签数组
 *   ctaText: string,
 *   ctaUrl: string,
 *   note?: string,
 *   heroImage: string,     // 各竞品子页不同 Unsplash 图 URL
 *   topChipText?: string,  // 左上角悬浮 chip 文字
 *   benefitCard?: { title, desc },     // 左侧浮动卡片
 *   bottomCard?: { label, title, desc } // 底部浮动卡片
 * }
 */
export function VsHero({ styles, onCtaClick }) {
  const { VS_HERO } = useContext(ConstantsContext);
  if (!VS_HERO) return null;

  return (
    <section className={styles['zd-landing-banner']}>
      <div className={styles['zd-landing-container']}>
        {/* 左侧文案 */}
        <div className={styles['zd-landing-copy']}>
          {VS_HERO.eyebrow && (
            <div className={styles['zd-eyebrow']}>
              <span />
              {VS_HERO.eyebrow}
            </div>
          )}
          <h1 className={styles['zd-title']}>
            {VS_HERO.title}{' '}
            <strong>{VS_HERO.titleHighlight}</strong>
          </h1>
          {VS_HERO.subtitle && (
            <p className={styles['zd-subtitle']}>{VS_HERO.subtitle}</p>
          )}
          {VS_HERO.desc && (
            <p className={styles['zd-desc']}>{VS_HERO.desc}</p>
          )}
          {/* Proof row */}
          {VS_HERO.proofItems && VS_HERO.proofItems.length > 0 && (
            <div className={styles['zd-proof-row']}>
              {VS_HERO.proofItems.map(function (item, i) {
                return (
                  <span key={i} className={styles['zd-proof']}>
                    <i>✓</i>
                    {item}
                  </span>
                );
              })}
            </div>
          )}
          <div className={styles['zd-actions']}>
            <button
              className={styles['zd-primary-btn']}
              onClick={function () {
                if (onCtaClick) onCtaClick(VS_HERO.ctaUrl || '/home');
              }}
            >
              {VS_HERO.ctaText || 'Try for Free'}
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: '18px'}}>
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"></path>
              </svg>
            </button>
            {VS_HERO.note && (
              <span className={styles['zd-note']}>{VS_HERO.note}</span>
            )}
          </div>
        </div>

        {/* 右侧图片区域 */}
        <div className={styles['zd-landing-visual']}>
          <div className={styles['zd-image-frame']}>
            {VS_HERO.heroImage && (
              <img
                src={VS_HERO.heroImage}
                alt={`CJdropshipping vs ${VS_HERO.titleHighlight}`}
                loading="eager"
              />
            )}
            {/* 左上角 chip */}
            {VS_HERO.topChipText && (
              <div className={styles['zd-left-chip']}>
                <i />
                <span>{VS_HERO.topChipText}</span>
              </div>
            )}
            {/* 左侧浮动 benefit 卡片 */}
            {VS_HERO.benefitCard && (
              <div className={styles['zd-benefit-card']}>
                <b>{VS_HERO.benefitCard.title}</b>
                <span>{VS_HERO.benefitCard.desc}</span>
              </div>
            )}
          </div>
          {/* 底部浮动卡片 */}
          {VS_HERO.bottomCard && (
            <div className={styles['zd-bottom-card']}>
              {VS_HERO.bottomCard.label && (
                <span className={styles['zd-bottom-label']}>
                  {VS_HERO.bottomCard.label}
                </span>
              )}
              {VS_HERO.bottomCard.title && (
                <p className={styles['zd-bottom-title-text']}>
                  {VS_HERO.bottomCard.title}
                </p>
              )}
              {VS_HERO.bottomCard.desc && (
                <p className={styles['zd-bottom-desc']}>{VS_HERO.bottomCard.desc}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ========== VsComparisonTable ==========

/**
 * 三列特征对比表（Feature / Competitor / CJdropshipping）
 * 从 ConstantsContext.VS_TABLE 读取文案：
 * {
 *   kicker: string,
 *   title: string,   // 支持 <strong> 标签
 *   desc?: string,
 *   summaryCards: [{ icon, title, desc }],  // 3 张摘要卡片
 *   rows: [{ feature, competitor, cj }],    // 对比数据行
 *   bottomText?: string
 * }
 */
export function VsComparisonTable({ styles }) {
  const { VS_TABLE } = useContext(ConstantsContext);
  if (!VS_TABLE) return null;

  return (
    <section className={styles['cj-vs-section']}>
      <div className={styles['cj-vs-container']}>
        {/* 标题 */}
        <div className={styles['section-header']}>
          {VS_TABLE.kicker && (
            <span className={styles['kicker']}>
              <i />
              {VS_TABLE.kicker}
            </span>
          )}
          <h2
            className={styles['section-title']}
            dangerouslySetInnerHTML={{ __html: VS_TABLE.title }}
          />
          {VS_TABLE.desc && (
            <p className={styles['section-desc']}>{VS_TABLE.desc}</p>
          )}
        </div>

        {/* 摘要卡片 */}
        {VS_TABLE.summaryCards && VS_TABLE.summaryCards.length > 0 && (
          <div className={styles['cj-vs-summary']}>
            {VS_TABLE.summaryCards.map(function (card, i) {
              return (
                <div key={i} className={styles['cj-vs-summary-card']}>
                  <div className={styles['cj-vs-summary-icon']}>{card.icon || i + 1}</div>
                  <h3>{card.title}</h3>
                  <p>{card.desc}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* 对比表格 */}
        <div className={styles['cj-vs-table-wrap']}>
          <table className={styles['cj-vs-table']}>
            <thead>
              <tr>
                <th>{VS_TABLE.headerFeature || 'Feature'}</th>
                <th className={styles['cj-col']}>
                  {VS_TABLE.headerCj || 'CJdropshipping'}
                  <span className={styles['cj-vs-recommend']}>Recommended</span>
                </th>
                <th className={styles['comp-col']}>
                  {VS_TABLE.headerCompetitor || 'Competitor'}
                </th>
              </tr>
            </thead>
            <tbody>
              {(VS_TABLE.rows || []).map(function (row, i) {
                return (
                  <tr key={i}>
                    <td className={styles['feat-col']}>{row.feature}</td>
                    <td className={styles['cj-col']}>{row.cj}</td>
                    <td className={styles['comp-col']}>{row.competitor}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {VS_TABLE.bottomText && (
          <div className={styles['cj-vs-bottom']}>
            <p>{VS_TABLE.bottomText}</p>
            {VS_TABLE.bottomCtaText && (
              <a
                className={styles['cj-vs-btn']}
                href={VS_TABLE.bottomCtaUrl || '/home'}
              >
                {VS_TABLE.bottomCtaText}
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ========== VsFaqSection ==========

/**
 * 子页专属 FAQ（左侧摘要卡片 + 右侧 accordion）
 * 【注意】使用 React state 控制展开/折叠，替代 HTML <details>，避免 SSR hydration 差异。
 * 默认第一条 FAQ 展开（openItems 初始值为 [0]）。
 *
 * 从 ConstantsContext.VS_FAQ 读取文案：
 * {
 *   kicker: string,
 *   title: string,    // 支持 <strong>
 *   intro?: string,
 *   sideCard: {
 *     label: string,
 *     title: string,
 *     desc: string,
 *     miniItems: [{ text }]
 *   },
 *   items: [{ q, a }],
 *   bottomText?: string,
 *   bottomCtaText?: string,
 *   bottomCtaUrl?: string
 * }
 */
export function VsFaqSection({ styles, onCtaClick }) {
  const { VS_FAQ } = useContext(ConstantsContext);
  // 默认展开第一条
  const [openItems, setOpenItems] = useState([0]);

  const toggleItem = useCallback(function (index) {
    setOpenItems(function (prev) {
      if (prev.indexOf(index) > -1) {
        return prev.filter(function (i) { return i !== index; });
      }
      return prev.concat([index]);
    });
  }, []);

  if (!VS_FAQ) return null;

  return (
    <section className={styles['zd-faq-section']}>
      <div className={styles['zd-faq-container']}>
        {/* 标题区域 */}
        <div className={styles['zd-faq-header']}>
          {VS_FAQ.kicker && (
            <div className={styles['zd-faq-kicker']}>
              <span />
              {VS_FAQ.kicker}
            </div>
          )}
          <h2
            className={styles['zd-faq-title']}
            dangerouslySetInnerHTML={{ __html: VS_FAQ.title }}
          />
          {VS_FAQ.intro && (
            <p className={styles['zd-faq-intro']}>{VS_FAQ.intro}</p>
          )}
        </div>

        {/* 主体：左侧摘要卡片 + 右侧 FAQ list */}
        <div className={styles['zd-faq-main']}>
          {/* 左侧摘要卡片 */}
          {VS_FAQ.sideCard && (
            <aside className={styles['zd-faq-side']}>
              <div className={styles['zd-faq-side-card']}>
                {VS_FAQ.sideCard.label && (
                  <div className={styles['zd-faq-side-label']}>
                    <span />
                    {VS_FAQ.sideCard.label}
                  </div>
                )}
                {VS_FAQ.sideCard.title && (
                  <h3>{VS_FAQ.sideCard.title}</h3>
                )}
                {VS_FAQ.sideCard.desc && (
                  <p>{VS_FAQ.sideCard.desc}</p>
                )}
                {VS_FAQ.sideCard.miniItems && VS_FAQ.sideCard.miniItems.length > 0 && (
                  <div className={styles['zd-faq-mini-list']}>
                    {VS_FAQ.sideCard.miniItems.map(function (item, i) {
                      return (
                        <div key={i} className={styles['zd-faq-mini-item']}>
                          <i>✓</i>
                          <span>{item.text}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </aside>
          )}

          {/* 右侧 FAQ accordion */}
          <div>
            <div className={styles['zd-faq-list']}>
              {(VS_FAQ.items || []).map(function (item, i) {
                const isOpen = openItems.indexOf(i) > -1;
                return (
                  <div
                    key={i}
                    className={[
                      styles['zd-faq-item'],
                      isOpen ? styles['zd-faq-item-open'] : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <button
                      className={styles['zd-faq-summary']}
                      onClick={function () { toggleItem(i); }}
                    >
                      <span className={styles['zd-faq-num']}>Q{i + 1}</span>
                      <span className={styles['zd-faq-question-text']}>{item.q}</span>
                      <span className={[styles['zd-faq-plus'], isOpen ? styles['zd-faq-plus-open'] : ''].filter(Boolean).join(' ')} />
                    </button>
                    {isOpen && (
                      <div className={styles['zd-faq-answer']}>
                        <p>{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 底部 CTA bar */}
            {(VS_FAQ.bottomText || VS_FAQ.bottomCtaText) && (
              <div className={styles['zd-faq-bottom']}>
                {VS_FAQ.bottomText && <p>{VS_FAQ.bottomText}</p>}
                {VS_FAQ.bottomCtaText && (
                  <button
                    className={styles['zd-faq-btn']}
                    onClick={function () {
                      if (onCtaClick) onCtaClick(VS_FAQ.bottomCtaUrl || '/home');
                    }}
                  >
                    {VS_FAQ.bottomCtaText}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
